import React, { Suspense, lazy, useState, useEffect } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import { NotificationProvider } from "./contextApi/NotificationProvider";
import Sidebar from "./layout/Sidebar";
import NewAssessment from "./pages/assessments/NewAssessment";
import LoginPage from "./pages/authentication/LoginPage";
import SignupPage from "./pages/authentication/SignupPage";
import PricingPlans from "./pages/subscription/Pricing";
import ProtectedRoute from "./components/ProtectedRoute";
import { useDispatch, useSelector } from "react-redux";
import "./App.css";
import ViewReport from "./pages/reports/ViewReport";
import ViewCandidateReport from "./pages/reports/ViewCandidateReport";
import AlertFailurePage from "./pages/subscription/AlertFailurePage";
import PaymentSuccess from "./pages/subscription/PaymentSuccess";
import GetFreeCredits from "./pages/GetFreeCredits/GetFreeCredits";
import FeedbackForm from "./pages/feedback/FeedbackForm";
import EmailTemplateSelector from "./components/EmailTemplateSelector";
import ConductedReportTabs from "./pages/reports/ConductedReportTabs";
import FloatingHelpButton from "./components/FloatingHelpButton";
import AddAdminStaff from "./pages/admin/AddAdminStaff";
import UserHelpCenterInfo from "./components/UserHelpCenterInfo";
import PartnerLead from "./pages/seoData/PartnerLead";
import IndividualAssessment from "./pages/assessments/IndividualAssessment";
import StudentConductedReportsTab from "./pages/reports/StudentConductedReportsTab";
import IndividualHelpCenterInfo from "./components/IndividualHelpCenterInfo";
import UpgradePlanModal from "./pages/ModalPopUp/UpgradePlanModal";
import AssessmentHistory from "./pages/settings/AssessmentHistory";
import OpenPositions from "./pages/openPositions/OpenPositions";
import Certificate from "./pages/certificate/Certificate";
import TourManager from "./components/TourManager/TourManager";
import { checkTourStatus } from "./redux/ReduxSlices/authSlice";
import useIsMobile from "./useIsMobile";
import NotFound from "./pages/NotFound";

const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));
const Assessments = lazy(() => import("./pages/assessments/Assessments"));
const AssignRole = lazy(() => import("./pages/assessments/AssignRole"));
const Analytics = lazy(() => import("./pages/analytics/Analytics"));
const Report = lazy(() => import("./pages/reports/Report"));
const Settings = lazy(() => import("./pages/settings/Settings"));

const SeoData = lazy(() => import("./pages/seoData/SeoData"));

const addAdmin = lazy(() => import("./pages/admin/AddAdminStaff"));

// Position
const OpenNewPostion = lazy(() =>
  import("./pages/openNewPosition/openNewPosition")
);
const ViewOpenPostion = lazy(() =>
  import("./pages/openNewPosition/viewOpenPosition")
);

const App = () => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const { userData, tourStatus } = useSelector((state) => state.auth);
  const [showReview, setShowReview] = useState(false);
  const [viewSideBar, setViewSidebar] = useState(true);
  const [tourGuideStatus, setTourGuideStatus] = useState(false)
  const location = useLocation();
  const dispatch = useDispatch();
  const isMobile = useIsMobile(768);

  useEffect(() => {
    if (location.pathname.includes("view-candidate-report")) {
      setViewSidebar(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(checkTourStatus())
    }
  }, [isAuthenticated])

  useEffect(() => {
    const ratingFlag = localStorage.getItem("rating");

    if (ratingFlag === "false") {
      const timer = setTimeout(() => {
        setShowReview(true);
      }, 30 * 60 * 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <NotificationProvider>
      {/* {showReview && (
        <FeedbackForm
          feedbackPopup={showReview}
          setFeedbackPopup={setShowReview}
        />
      )} */}
      {/* <Router> */}
      <Suspense fallback={<div className="p-6 text-center"></div>}>
        <Routes>
          {isAuthenticated ? (
            <Route
              path="/*"
              element={
                <div className="flex">
                  {viewSideBar && <Sidebar />}
                  <div
                    className="flex-1 lg:ml-0 h-screen overflow-y-auto bg-gradient-to-br from-white to-gray-50"
                    id="content-scroll"
                  >
                    {tourStatus.fresh_user && tourStatus.user_pk !== "null" && tourStatus.user_pk !== null && !isMobile && <TourManager />}
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute
                            allowedRoles={[
                              "hr",
                              "manager",
                              "executive",
                              "owner",
                              "cfo",
                              "individual",
                            ]}
                          >
                            <Dashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/assessments"
                        element={
                          <ProtectedRoute
                            allowedRoles={["hr", "manager", "owner", "cfo"]}
                          >
                            <Assessments />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/individual-assessment"
                        element={
                          <ProtectedRoute allowedRoles={["individual"]}>
                            <IndividualAssessment />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/assign-role"
                        element={
                          <ProtectedRoute
                            allowedRoles={["hr", "manager", "owner", "cfo"]}
                          >
                            <AssignRole />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/analytics"
                        element={
                          <ProtectedRoute
                            allowedRoles={["hr", "manager", "owner", "cfo"]}
                          >
                            <Analytics />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/report"
                        element={
                          <ProtectedRoute
                            allowedRoles={[
                              "hr",
                              "manager",
                              "executive",
                              "owner",
                              "cfo",
                            ]}
                          >
                            {/* <Report /> */}
                            <ConductedReportTabs />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/student-report"
                        element={
                          <ProtectedRoute allowedRoles={["individual"]}>
                            {/* <Report /> */}
                            <StudentConductedReportsTab />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/pricing"
                        element={
                          <ProtectedRoute
                            allowedRoles={["hr", "owner", "cfo", "individual"]}
                          >
                            <PricingPlans />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/add-admin"
                        element={
                          <ProtectedRoute
                            allowedRoles={["hr", "owner", "cfo"]}
                          >
                            <AddAdminStaff />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/settings"
                        element={
                          <ProtectedRoute
                            allowedRoles={[
                              "hr",
                              "executive",
                              "manager",
                              "owner",
                              "cfo",
                              "individual",
                            ]}
                          >
                            <Settings />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/assessment-history"
                        element={
                          <ProtectedRoute
                            allowedRoles={[
                              "hr",
                              "executive",
                              "manager",
                              "owner",
                              "cfo",
                              "individual",
                            ]}
                          >
                            <AssessmentHistory />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/seo-data"
                        element={
                          <ProtectedRoute allowedRoles={["hr", "owner", "cfo"]}>
                            <SeoData />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/partner-lead"
                        element={
                          <ProtectedRoute allowedRoles={["hr", "owner", "cfo"]}>
                            <PartnerLead />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/email-template"
                        element={
                          <ProtectedRoute
                            allowedRoles={[
                              "hr",
                              "manager",
                              "owner",
                              "cfo",
                              "individual",
                            ]}
                          >
                            <EmailTemplateSelector />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/assessments/new"
                        element={
                          <ProtectedRoute
                            allowedRoles={["hr", "owner", "cfo", "individual"]}
                          >
                            <NewAssessment />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/viewreport/:id"
                        element={
                          <ProtectedRoute
                            allowedRoles={[
                              "hr",
                              "executive",
                              "manager",
                              "owner",
                              "cfo",
                              "individual",
                            ]}
                          >
                            <ViewReport />
                          </ProtectedRoute>
                        }
                      />
                      {/* <Route
                        path="/hire-through-vendor"
                        element={
                          <ProtectedRoute allowedRoles={["hr", "owner", "cfo"]}>
                            <OpenNewPostion />
                          </ProtectedRoute>
                        }
                      /> */}
                      <Route
                        path="/open-positions"
                        element={
                          <ProtectedRoute allowedRoles={["hr", "owner", "cfo"]}>
                            <OpenPositions />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/get-free-credits"
                        element={
                          <ProtectedRoute
                            allowedRoles={[
                              "hr",
                              "owner",
                              "cfo",
                              "manager",
                              "individual",
                            ]}
                          >
                            <GetFreeCredits />
                          </ProtectedRoute>
                        }
                      />
                      {/* <Route
                        path="/view-open-position"
                        element={
                          <ProtectedRoute
                            allowedRoles={["hr", "owner", "cfo", "manager"]}
                          >
                            <ViewOpenPostion />
                          </ProtectedRoute>
                        }
                      /> */}
                      <Route path="/login" element={<Navigate to="/" />} />
                      <Route path="/sign-up" element={<Navigate to="/" />} />
                      <Route
                        path="/payment-failure"
                        element={
                          <ProtectedRoute
                            allowedRoles={[
                              "hr",
                              "manager",
                              "owner",
                              "cfo",
                              "individual",
                            ]}
                          >
                            <AlertFailurePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/payment-success"
                        element={
                          <ProtectedRoute
                            allowedRoles={[
                              "hr",
                              "manager",
                              "owner",
                              "cfo",
                              "individual",
                            ]}
                          >
                            <PaymentSuccess />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/helpinfo" element={<UserHelpCenterInfo />} />
                      <Route
                        path="/helpinfo-individual"
                        element={<IndividualHelpCenterInfo />}
                      />
                      <Route
                        path="/view-candidate-report/:id"
                        element={<ViewCandidateReport />}
                      />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </div>
                  {/* Global floating help */}
                  <FloatingHelpButton />

                  <UpgradePlanModal userData={userData} />
                </div>
              }
            />
          ) : (
            <>
              <Route
                path="/view-candidate-report/:id"
                element={<ViewCandidateReport />}
              />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/sign-up" element={<SignupPage />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </>
          )}
        </Routes>
      </Suspense>
      {/* </Router> */}
    </NotificationProvider>
  );
};

export default App;
